import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { getTasksAppStore } from './tasksAppStore';

/**
 * AI Commands for Tasks App
 * Self-contained command definitions for AI integration
 */

export const createTaskCommand: SelfContainedCommand = {
  id: 'tasks_create_task',
  description: 'Create a new task in the Tasks app with optional project, area, priority, tags, and due date',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Task title (required)',
        required: true,
        examples: ['Buy groceries', 'Call dentist', 'Finish project proposal']
      },
      {
        name: 'notes',
        type: 'string',
        description: 'Optional task notes or description',
        required: false,
        examples: ['Remember to get organic vegetables', 'Schedule for next week']
      },
      {
        name: 'status',
        type: 'string',
        description: 'Task status: backlog, todo, in-progress, done, or canceled',
        required: false,
        defaultValue: 'todo',
        examples: ['todo', 'in-progress', 'backlog']
      },
      {
        name: 'dueDate',
        type: 'string',
        description: 'Due date in YYYY-MM-DD format',
        required: false,
        pattern: /^\d{4}-\d{2}-\d{2}$/,
        examples: ['2024-03-15', '2024-12-31']
      },
      {
        name: 'priority',
        type: 'string',
        description: 'Task priority level: low, medium, or high',
        required: false,
        defaultValue: 'medium',
        examples: ['low', 'medium', 'high']
      },
      {
        name: 'tags',
        type: 'array',
        description: 'Array of tags for the task',
        required: false,
        examples: [['work', 'urgent'], ['personal'], ['shopping']]
      },
      {
        name: 'projectId',
        type: 'string',
        description: 'Optional project ID to assign this task to',
        required: false,
        examples: ['proj_abc123']
      },
      {
        name: 'areaId',
        type: 'string',
        description: 'Optional area ID to assign this task to',
        required: false,
        examples: ['area_xyz789']
      },
      {
        name: 'isInToday',
        type: 'boolean',
        description: 'Whether to add this task to Today view',
        required: false,
        defaultValue: false
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID where the task should be created (auto-filled in standalone mode)',
        required: false
      }
    ],
    contextRequirements: {
      requiresActiveWindow: true
    }
  },
  
  examples: {
    good: [
      {
        input: {
          title: 'Review quarterly report',
          notes: 'Focus on sales figures and growth metrics',
          dueDate: '2024-03-20',
          priority: 'high',
          tags: ['work', 'quarterly'],
          windowId: 'win123',
          spaceId: 'space456'
        },
        expectedOutput: {
          status: 'success',
          message: '✅ Task "Review quarterly report" created successfully',
          format: 'json',
          data: { taskId: 'task789' }
        },
        description: 'Complete task creation with all optional fields'
      }
    ],
    bad: [
      {
        input: { title: '', windowId: 'win123', spaceId: 'space456' },
        error: 'Task title cannot be empty',
        why: 'Title is required for task creation',
        fix: 'Provide a meaningful task title'
      }
    ]
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { 
        title, 
        notes, 
        status = 'todo',
        dueDate, 
        priority = 'medium', 
        tags = [], 
        projectId,
        areaId,
        isInToday = false
      } = params;
      
      // ✅ Auto-fill windowId and spaceId from context (for standalone mode)
      const windowId = params.windowId || 'standalone-tasks';
      const spaceId = params.spaceId || context.spaceId || 'default';
      
      console.log('[tasks_create_task] 🏗️ Execution context:', {
        providedWindowId: params.windowId,
        providedSpaceId: params.spaceId,
        contextSpaceId: context.spaceId,
        finalWindowId: windowId,
        finalSpaceId: spaceId
      });
      
      if (!title?.trim()) {
        return createErrorResult(
          'Task creation failed',
          'Task title cannot be empty',
          'validation'
        );
      }
      
      const store = getTasksAppStore(windowId, spaceId);
      const task = await store.getState().createTask({
        title: title.trim(),
        notes: notes?.trim(),
        status: status as any,
        dueDate,
        priority: priority as 'low' | 'medium' | 'high',
        tags: Array.isArray(tags) ? tags : [],
        projectId,
        areaId,
        isInToday
      });
      
      if (task) {
        // Build a helpful message about where the task was created
        let locationMsg = '';
        if (projectId) locationMsg += ' in project';
        if (areaId) locationMsg += ` in area`;
        if (isInToday) locationMsg += ' (added to Today)';
        
        return createSuccessResult(
          `✅ Task "${task.name}" created successfully${locationMsg}`,
          { 
            taskId: task._id, 
            title: task.name,
            priority: task.skeleton.priority,
            dueDate: task.skeleton.dueDate,
            tags: task.skeleton.tags
          },
          {
            format: 'markdown',
            ui: { refreshContext: true }
          }
        );
      } else {
        return createErrorResult(
          'Task creation failed',
          'Unable to create task - please try again',
          'execution'
        );
      }
    } catch (error) {
      return createErrorResult(
        'Task creation failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

export const completeTaskCommand: SelfContainedCommand = {
  id: 'tasks_complete_task',
  description: 'Mark a task as completed',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'taskId',
        type: 'string',
        description: 'ID of the task to complete',
        required: true
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID containing the task (auto-filled in standalone mode)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { taskId: 'task123', windowId: 'win456', spaceId: 'space789' },
        expectedOutput: {
          status: 'success',
          message: '✅ Task completed successfully',
          format: 'json'
        },
        description: 'Mark task as complete'
      }
    ],
    bad: [
      {
        input: { taskId: '', windowId: 'win456', spaceId: 'space789' },
        error: 'Task ID is required',
        why: 'Cannot complete task without valid ID',
        fix: 'Provide a valid task ID'
      }
    ]
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { taskId } = params;
      
      // ✅ Auto-fill windowId and spaceId from context (for standalone mode)
      const windowId = params.windowId || 'standalone-tasks';
      const spaceId = params.spaceId || context.spaceId || 'default';
      
      if (!taskId?.trim()) {
        return createErrorResult(
          'Task completion failed',
          'Task ID is required',
          'validation'
        );
      }
      
      const store = getTasksAppStore(windowId, spaceId);
      await store.getState().completeTask(taskId);
      
      return createSuccessResult(
        '✅ Task completed successfully',
        { taskId },
        {
          format: 'markdown',
          ui: { refreshContext: true }
        }
      );
    } catch (error) {
      return createErrorResult(
        'Task completion failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

export const createProjectCommand: SelfContainedCommand = {
  id: 'tasks_create_project',
  description: 'Create a new project in the Tasks app. IMPORTANT: When creating a project, also create a list of specific, actionable tasks by including them in the tasks array. Break down the project into concrete steps with clear titles.',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Project title (required)',
        required: false,
        defaultValue: 'Untitled Project',
        examples: ['Website Redesign', 'Marketing Campaign', 'Product Launch']
      },
      {
        name: 'notes',
        type: 'string',
        description: 'Optional project notes or description',
        required: false
      },
      {
        name: 'areaId',
        type: 'string',
        description: 'Optional area ID to associate the project with',
        required: false
      },
      {
        name: 'dueDate',
        type: 'string',
        description: 'Project due date in YYYY-MM-DD format',
        required: false,
        pattern: /^\d{4}-\d{2}-\d{2}$/
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Optional folder ID to create the project in',
        required: false
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID where the project should be created (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'tasks',
        type: 'array',
        description: 'Optional array of initial tasks to create for this project. Each task object should have: title (string, required), notes (string, optional), priority (low/medium/high, optional), dueDate (YYYY-MM-DD, optional), tags (array, optional)',
        required: false,
        examples: [
          [
            { title: 'Design mockups', priority: 'high' },
            { title: 'Write documentation', priority: 'medium', tags: ['docs'] },
            { title: 'Setup deployment', priority: 'low', dueDate: '2024-12-31' }
          ]
        ]
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          title: 'Mobile App Development',
          notes: 'iOS and Android versions',
          dueDate: '2024-06-30',
          tasks: [
            { title: 'Design UI mockups', priority: 'high' },
            { title: 'Develop backend API', priority: 'high' },
            { title: 'Test on devices', priority: 'medium' }
          ],
          windowId: 'win123',
          spaceId: 'space456'
        },
        expectedOutput: {
          status: 'success',
          message: '✅ Project "Mobile App Development" created successfully with 3 tasks',
          format: 'json'
        },
        description: 'Create project with tasks - this is the preferred approach'
      }
    ],
    bad: [
      {
        input: { title: '', windowId: 'win123', spaceId: 'space456' },
        error: 'Project title cannot be empty',
        why: 'Title is required for project creation',
        fix: 'Provide a meaningful project title'
      }
    ]
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { title = 'Untitled Project', notes, areaId, dueDate, folderId, tasks: initialTasks } = params;
      
      // ✅ Auto-fill windowId and spaceId from context (for standalone mode)
      const windowId = params.windowId || 'standalone-tasks';
      const spaceId = params.spaceId || context.spaceId || 'default';
      
      console.log('[tasks_create_project] 🏗️ Creating project:', {
        title,
        windowId,
        spaceId,
        folderId,
        hasInitialTasks: !!initialTasks,
        initialTasksCount: Array.isArray(initialTasks) ? initialTasks.length : 0
      });
      
      // ✅ Create project using entityStore.actions for proper store integration
      const useEntityStore = (await import('../../../stores/entityStore')).default;
      const entityStore = useEntityStore.getState();
      
            const project = await entityStore.actions.createEntity({
              entityType: 'Project',
              spaceId,
              name: title.trim(),
              icon: 'Circle',
              skeleton: {
                '@type': 'Project',
                notes: notes?.trim() || '',
                completed: false,
                order: Date.now(),
                ...(areaId && { areaId }),
                ...(dueDate && { dueDate })
              }
            } as any);
      
      // ✅ No need to manually add - entity store subscription will handle it
      if (project) {
        console.log('[tasks_create_project] ✅ Project created:', {
          projectId: project._id,
          title: project.name
        });
        
        // Handle folder relationship if folderId provided
        if (folderId) {
          try {
            const entityStoreModule = await import('../../../stores/entityStore');
            const entityStore = entityStoreModule.default.getState();
            
            const folder = entityStore.entities.find(e => e._id === folderId);
            
            if (folder?.entityType === 'Group') {
              const entityId = project._id;
              const currentChildren = folder.skeleton?.childEntities || [];
              
              await entityStore.actions.updateEntity(folderId, {
                skeleton: {
                  childEntities: [...currentChildren, entityId]
                }
              });
              
              await entityStore.actions.updateEntity(entityId, {
                skeleton: {
                  groupIds: [folderId]
                }
              });
              
              console.log('[tasks_create_project] 📁 Added to folder:', folderId);
            }
          } catch (error) {
            console.error('[tasks_create_project] ⚠️ Failed to add to folder:', error);
          }
        }
        
        // Create initial tasks if provided
        const createdTasks = [];
        if (Array.isArray(initialTasks) && initialTasks.length > 0) {
          console.log('[tasks_create_project] 🔨 Creating initial tasks:', initialTasks.length);
          
          // Get store for task creation
          const store = getTasksAppStore(windowId, spaceId);
          
          for (const taskData of initialTasks) {
            try {
              const task = await store.getState().createTask({
                title: taskData.title,
                notes: taskData.notes,
                priority: taskData.priority as any,
                dueDate: taskData.dueDate,
                tags: taskData.tags || [],
                projectId: project._id, // Assign to this project
                areaId: areaId // Inherit area from project
              });
              
              if (task) {
                createdTasks.push(task);
                console.log('[tasks_create_project] ✅ Created task:', task.name);
              }
            } catch (error) {
              console.error('[tasks_create_project] ⚠️ Failed to create task:', taskData.title, error);
            }
          }
        }
        
        // ✅ Wait briefly for entity to propagate to entityStore before opening window
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // ✅ Open window for the project
        let windowInfo = null;
        try {
          const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
          const { executeCommand: commandExecute } = useCommandRegistryStore.getState();
          
          const entityId = project._id;
          
          console.log('[tasks_create_project] 🪟 Opening window for project:', {
            entityId,
            entityType: project.entityType,
            name: project.name
          });
          
          const windowResult = (await commandExecute('space_open_window', {
            entityId
          })) as StandardToolResult;
          
          if (windowResult.status === 'success') {
            windowInfo = windowResult.data;
            console.log('[tasks_create_project] ✅ Window opened:', windowInfo);
          } else {
            console.error('[tasks_create_project] ⚠️ Failed to open window:', windowResult.message);
          }
        } catch (error) {
          console.error('[tasks_create_project] ⚠️ Error opening window:', error);
        }
        
        const tasksMessage = createdTasks.length > 0 
          ? ` with ${createdTasks.length} task${createdTasks.length !== 1 ? 's' : ''}`
          : '';
        
        return createSuccessResult(
          `✅ Project "${project.name}" created successfully${tasksMessage}${windowInfo ? ' (opened)' : ''}`,
          { 
            entityId: project._id,  // ✅ Return entityId for EntitySidebar
            projectId: project._id, 
            name: project.name,
            title: project.name,
            tasksCreated: createdTasks.length,
            taskTitles: createdTasks.map(t => t.name),
            windowId: windowInfo?.windowId
          },
          {
            format: 'markdown',
            ui: { 
              refreshContext: true,
              autoFocus: windowInfo?.windowId
            }
          }
        );
      } else {
        return createErrorResult(
          'Project creation failed',
          'Unable to create project - please try again',
          'execution'
        );
      }
    } catch (error) {
      return createErrorResult(
        'Project creation failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

export const createAreaCommand: SelfContainedCommand = {
  id: 'tasks_create_area',
  description: 'Create a new area in the Tasks app',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Area title (required)',
        required: true,
        examples: ['Work', 'Personal', 'Health', 'Finance']
      },
      {
        name: 'notes',
        type: 'string',
        description: 'Optional area notes or description',
        required: false
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID where the area should be created',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          title: 'Health & Fitness',
          notes: 'Exercise, nutrition, and wellness tracking',
          windowId: 'win123',
          spaceId: 'space456'
        },
        expectedOutput: {
          status: 'success',
          message: '✅ Area "Health & Fitness" created successfully',
          format: 'json'
        },
        description: 'Create area with description'
      }
    ],
    bad: [
      {
        input: { title: '', windowId: 'win123', spaceId: 'space456' },
        error: 'Area title cannot be empty',
        why: 'Title is required for area creation',
        fix: 'Provide a meaningful area title'
      }
    ]
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { title, notes } = params;
      
      // ✅ Auto-fill windowId and spaceId from context (for standalone mode)
      const windowId = params.windowId || 'standalone-tasks';
      const spaceId = params.spaceId || context.spaceId || 'default';
      
      if (!title?.trim()) {
        return createErrorResult(
          'Area creation failed',
          'Area title cannot be empty',
          'validation'
        );
      }
      
      const store = getTasksAppStore(windowId, spaceId);
      const area = await store.getState().createArea({
        title: title.trim(),
        notes: notes?.trim()
      });
      
      if (area) {
        return createSuccessResult(
          `✅ Area "${area.name}" created successfully`,
          { areaId: area._id, title: area.name },
          {
            format: 'markdown',
            ui: { refreshContext: true }
          }
        );
      } else {
        return createErrorResult(
          'Area creation failed',
          'Unable to create area - please try again',
          'execution'
        );
      }
    } catch (error) {
      return createErrorResult(
        'Area creation failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

export const moveTaskToTodayCommand: SelfContainedCommand = {
  id: 'tasks_move_to_today',
  description: 'Move a task to Today view',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'taskId',
        type: 'string',
        description: 'ID of the task to move to Today',
        required: true
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID containing the task',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { taskId: 'task123', windowId: 'win456', spaceId: 'space789' },
        expectedOutput: {
          status: 'success',
          message: '⭐ Task moved to Today successfully',
          format: 'json'
        },
        description: 'Move task to Today view'
      }
    ],
    bad: [
      {
        input: { taskId: '', windowId: 'win456', spaceId: 'space789' },
        error: 'Task ID is required',
        why: 'Cannot move task without valid ID',
        fix: 'Provide a valid task ID'
      }
    ]
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { taskId } = params;
      
      // ✅ Auto-fill windowId and spaceId from context (for standalone mode)
      const windowId = params.windowId || 'standalone-tasks';
      const spaceId = params.spaceId || context.spaceId || 'default';
      
      if (!taskId?.trim()) {
        return createErrorResult(
          'Move to Today failed',
          'Task ID is required',
          'validation'
        );
      }
      
      const store = getTasksAppStore(windowId, spaceId);
      await store.getState().moveTaskToToday(taskId);
      
      return createSuccessResult(
        '⭐ Task moved to Today successfully',
        { taskId },
        {
          format: 'markdown',
          ui: { refreshContext: true }
        }
      );
    } catch (error) {
      return createErrorResult(
        'Move to Today failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

// Update Task Command - for revising/refining tasks
export const updateTaskCommand: SelfContainedCommand = {
  id: 'tasks_update_task',
  description: 'Update an existing task with new details (title, notes, priority, tags, due date, etc.)',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'taskId',
        type: 'string',
        description: 'ID of the task to update',
        required: true
      },
      {
        name: 'title',
        type: 'string',
        description: 'New task title',
        required: false
      },
      {
        name: 'notes',
        type: 'string',
        description: 'New task notes or description',
        required: false
      },
      {
        name: 'status',
        type: 'string',
        description: 'Task status: backlog, todo, in-progress, done, or canceled',
        required: false
      },
      {
        name: 'dueDate',
        type: 'string',
        description: 'Due date in YYYY-MM-DD format',
        required: false
      },
      {
        name: 'priority',
        type: 'string',
        description: 'Task priority: low, medium, or high',
        required: false
      },
      {
        name: 'tags',
        type: 'array',
        description: 'Array of tags for the task',
        required: false
      },
      {
        name: 'projectId',
        type: 'string',
        description: 'Project ID to assign task to',
        required: false
      },
      {
        name: 'areaId',
        type: 'string',
        description: 'Area ID to assign task to',
        required: false
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID containing the task',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          taskId: 'task123',
          title: 'Review Q1 report - UPDATED',
          priority: 'high',
          dueDate: '2024-03-25',
          tags: ['work', 'urgent', 'quarterly'],
          windowId: 'win456',
          spaceId: 'space789'
        },
        expectedOutput: {
          status: 'success',
          message: '✅ Task updated successfully',
          format: 'json'
        },
        description: 'Update multiple task fields'
      }
    ],
    bad: [
      {
        input: { taskId: '', title: 'New Title', windowId: 'win456', spaceId: 'space789' },
        error: 'Task ID is required',
        why: 'Cannot update task without valid ID',
        fix: 'Provide a valid task ID'
      }
    ]
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { taskId, windowId: _windowId, spaceId: _spaceId, ...updates } = params;
      
      const windowId = _windowId || 'standalone-tasks';
      const spaceId = _spaceId || context.spaceId || 'default';
      
      if (!taskId?.trim()) {
        return createErrorResult(
          'Task update failed',
          'Task ID is required',
          'validation'
        );
      }
      
      // Filter out undefined values
      const cleanUpdates: any = {};
      Object.keys(updates).forEach(key => {
        if (updates[key] !== undefined) {
          cleanUpdates[key] = updates[key];
        }
      });
      
      if (Object.keys(cleanUpdates).length === 0) {
        return createErrorResult(
          'Task update failed',
          'No update fields provided',
          'validation'
        );
      }
      
      const store = getTasksAppStore(windowId, spaceId);
      await store.getState().updateTask(taskId, cleanUpdates);
      
      return createSuccessResult(
        `✅ Task updated successfully`,
        { taskId, updatedFields: Object.keys(cleanUpdates) },
        {
          format: 'markdown',
          ui: { refreshContext: true }
        }
      );
    } catch (error) {
      return createErrorResult(
        'Task update failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

// List Tasks Command - for viewing existing tasks
export const listTasksCommand: SelfContainedCommand = {
  id: 'tasks_list_tasks',
  description: 'List tasks in the Tasks app. Can filter by view (inbox, today, upcoming, etc.), project, area, or search query',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'view',
        type: 'string',
        description: 'View to list tasks from: inbox, today, upcoming, anytime, someday, logbook, project, area, tags, search',
        required: false,
        defaultValue: 'inbox'
      },
      {
        name: 'projectId',
        type: 'string',
        description: 'Filter tasks by project ID',
        required: false
      },
      {
        name: 'areaId',
        type: 'string',
        description: 'Filter tasks by area ID',
        required: false
      },
      {
        name: 'completed',
        type: 'boolean',
        description: 'Filter by completion status (true = completed, false = active)',
        required: false
      },
      {
        name: 'tag',
        type: 'string',
        description: 'Filter tasks by tag',
        required: false
      },
      {
        name: 'searchQuery',
        type: 'string',
        description: 'Search tasks by title, notes, or tags',
        required: false
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID to list tasks from (auto-filled in standalone mode)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { view: 'today', windowId: 'win123', spaceId: 'space456' },
        expectedOutput: {
          status: 'success',
          message: 'Found 5 tasks in Today view',
          data: { tasks: [], count: 5 },
          format: 'json'
        },
        description: 'List today\'s tasks'
      }
    ],
    bad: []
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { view = 'inbox', projectId, areaId, completed, tag, searchQuery } = params;
      
      // ✅ Auto-fill windowId and spaceId from context (for standalone mode)
      const windowId = params.windowId || 'standalone-tasks';
      const spaceId = params.spaceId || context.spaceId || 'default';
      
      const store = getTasksAppStore(windowId, spaceId);
      const state = store.getState();
      
      let tasks = state.tasks;

      // When the caller requests completed tasks, use the full task list so view
      // filters (today/inbox/upcoming) that pre-exclude completed tasks don't hide them.
      if (completed !== undefined) {
        tasks = tasks.filter(t => t.skeleton.completed === completed);
      }
      
      // Apply view / scope filters
      if (view === 'today') {
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        tasks = tasks.filter(t => t.skeleton.isInToday || t.skeleton.dueDate === today);
      } else if (view === 'upcoming') {
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        tasks = tasks.filter(t => t.skeleton.dueDate && t.skeleton.dueDate > today);
      } else if (view === 'inbox') {
        tasks = tasks.filter(t => !t.skeleton.projectId && !t.skeleton.areaId && !t.skeleton.isInToday);
      } else if (view === 'logbook') {
        tasks = tasks.filter(t => t.skeleton.completed);
      } else if (projectId) {
        tasks = tasks.filter(t => t.skeleton.projectId === projectId);
      } else if (areaId) {
        tasks = tasks.filter(t => t.skeleton.areaId === areaId);
      } else if (tag) {
        tasks = tasks.filter(t => t.skeleton.tags.includes(tag));
      }
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        tasks = tasks.filter(t => 
          t.name.toLowerCase().includes(query) ||
          t.skeleton.notes?.toLowerCase().includes(query) ||
          t.skeleton.tags.some(tag => tag.toLowerCase().includes(query))
        );
      }
      
      // Format tasks for output
      const tasksList = tasks.map(t => ({
        id: t._id,
        title: t.name,
        status: t.skeleton.status,
        priority: t.skeleton.priority,
        dueDate: t.skeleton.dueDate,
        tags: t.skeleton.tags,
        projectId: t.skeleton.projectId,
        areaId: t.skeleton.areaId,
        notes: t.skeleton.notes?.substring(0, 100) // First 100 chars
      }));
      
      return createSuccessResult(
        `Found ${tasks.length} task${tasks.length !== 1 ? 's' : ''} in ${view} view`,
        { tasks: tasksList, count: tasks.length },
        {
          format: 'json',
          ui: { refreshContext: false }
        }
      );
    } catch (error) {
      return createErrorResult(
        'List tasks failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

// List Projects Command
export const listProjectsCommand: SelfContainedCommand = {
  id: 'tasks_list_projects',
  description: 'List all projects in the Tasks app',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'areaId',
        type: 'string',
        description: 'Optional: filter projects by area ID',
        required: false
      },
      {
        name: 'completed',
        type: 'boolean',
        description: 'Optional: filter by completion status',
        required: false
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID to list projects from',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { windowId: 'win123', spaceId: 'space456' },
        expectedOutput: {
          status: 'success',
          message: 'Found 3 projects',
          data: { projects: [], count: 3 },
          format: 'json'
        },
        description: 'List all projects'
      }
    ],
    bad: []
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { areaId, completed } = params;
      
      // ✅ Auto-fill windowId and spaceId from context (for standalone mode)
      const windowId = params.windowId || 'standalone-tasks';
      const spaceId = params.spaceId || context.spaceId || 'default';
      
      const store = getTasksAppStore(windowId, spaceId);
      let projects = store.getState().projects;
      
      // Apply filters
      if (areaId) {
        projects = projects.filter(p => p.skeleton.areaId === areaId);
      }
      
      if (completed !== undefined) {
        projects = projects.filter(p => p.skeleton.completed === completed);
      }
      
      const projectsList = projects.map(p => ({
        id: p._id,
        title: p.name,
        completed: p.skeleton.completed,
        areaId: p.skeleton.areaId,
        dueDate: p.skeleton.dueDate,
        notes: p.skeleton.notes?.substring(0, 100)
      }));
      
      return createSuccessResult(
        `Found ${projects.length} project${projects.length !== 1 ? 's' : ''}`,
        { projects: projectsList, count: projects.length },
        {
          format: 'json',
          ui: { refreshContext: false }
        }
      );
    } catch (error) {
      return createErrorResult(
        'List projects failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

// List Areas Command
export const listAreasCommand: SelfContainedCommand = {
  id: 'tasks_list_areas',
  description: 'List all areas in the Tasks app',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID to list areas from',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { windowId: 'win123', spaceId: 'space456' },
        expectedOutput: {
          status: 'success',
          message: 'Found 4 areas',
          data: { areas: [], count: 4 },
          format: 'json'
        },
        description: 'List all areas'
      }
    ],
    bad: []
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      // ✅ Auto-fill windowId and spaceId from context (for standalone mode)
      const windowId = params.windowId || 'standalone-tasks';
      const spaceId = params.spaceId || context.spaceId || 'default';
      
      const store = getTasksAppStore(windowId, spaceId);
      const areas = store.getState().areas;
      
      const areasList = areas.map(a => ({
        id: a._id,
        title: a.name,
        notes: a.skeleton.notes?.substring(0, 100)
      }));
      
      return createSuccessResult(
        `Found ${areas.length} area${areas.length !== 1 ? 's' : ''}`,
        { areas: areasList, count: areas.length },
        {
          format: 'json',
          ui: { refreshContext: false }
        }
      );
    } catch (error) {
      return createErrorResult(
        'List areas failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

// Update Project Command
export const updateProjectCommand: SelfContainedCommand = {
  id: 'tasks_update_project',
  description: 'Update an existing project with new details',
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'projectId',
        type: 'string',
        description: 'ID of the project to update',
        required: true
      },
      {
        name: 'title',
        type: 'string',
        description: 'New project title',
        required: false
      },
      {
        name: 'notes',
        type: 'string',
        description: 'New project notes',
        required: false
      },
      {
        name: 'areaId',
        type: 'string',
        description: 'Area ID to assign project to',
        required: false
      },
      {
        name: 'dueDate',
        type: 'string',
        description: 'Due date in YYYY-MM-DD format',
        required: false
      },
      {
        name: 'completed',
        type: 'boolean',
        description: 'Mark project as completed/incomplete',
        required: false
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Target window ID for the Tasks app (auto-filled in standalone mode)',
        required: false
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID containing the project',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          projectId: 'proj123',
          title: 'Updated Project Name',
          dueDate: '2024-06-30',
          windowId: 'win456',
          spaceId: 'space789'
        },
        expectedOutput: {
          status: 'success',
          message: '✅ Project updated successfully',
          format: 'json'
        },
        description: 'Update project details'
      }
    ],
    bad: []
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { projectId, windowId: _windowId, spaceId: _spaceId, ...updates } = params;
      
      const windowId = _windowId || 'standalone-tasks';
      const spaceId = _spaceId || context.spaceId || 'default';
      
      if (!projectId?.trim()) {
        return createErrorResult(
          'Project update failed',
          'Project ID is required',
          'validation'
        );
      }
      
      const cleanUpdates: any = {};
      Object.keys(updates).forEach(key => {
        if (updates[key] !== undefined) {
          cleanUpdates[key] = updates[key];
        }
      });
      
      if (Object.keys(cleanUpdates).length === 0) {
        return createErrorResult(
          'Project update failed',
          'No update fields provided',
          'validation'
        );
      }
      
      const store = getTasksAppStore(windowId, spaceId);
      await store.getState().updateProject(projectId, cleanUpdates);
      
      return createSuccessResult(
        `✅ Project updated successfully`,
        { projectId, updatedFields: Object.keys(cleanUpdates) },
        {
          format: 'markdown',
          ui: { refreshContext: true }
        }
      );
    } catch (error) {
      return createErrorResult(
        'Project update failed',
        error instanceof Error ? error.message : 'Unknown error occurred',
        'execution'
      );
    }
  }
};

// Export all commands
export const tasksCommands: SelfContainedCommand[] = [
  createTaskCommand,
  updateTaskCommand,
  completeTaskCommand,
  listTasksCommand,
  listProjectsCommand,
  listAreasCommand,
  createProjectCommand,
  updateProjectCommand,
  createAreaCommand,
  moveTaskToTodayCommand
];

// Register Project creation with Entity Creation Registry
import { entityCreationRegistry } from '../../../services/entityCreationRegistry';

entityCreationRegistry.register({
  commandId: 'tasks_create_project',
  entityType: 'Project',
  displayName: 'Project',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'Circle',
    phosphorWeight: 'regular'
  },
  description: 'Create a new TasksApp project',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'productivity',
  sortOrder: 20
});
